import { CloudIcon, LaptopIcon, ListIcon, LoaderIcon, RefreshCwIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { DiagnosticsTargetPicker } from "./DiagnosticsTargetPicker";
import type {
  DiagnosticsTargetGroup,
  DiagnosticsTargetOption,
} from "./DiagnosticsTargetPicker.logic";

export function DiagnosticsHeader({
  onRefresh,
  refreshing,
  canRefresh,
  isPlatformAdmin,
  targetGroups,
  activeTarget,
  onSelectTarget,
  eventsActive,
  onShowRuntime,
  onShowEvents,
}: {
  readonly onRefresh: () => void;
  readonly refreshing: boolean;
  readonly canRefresh: boolean;
  readonly isPlatformAdmin: boolean;
  readonly targetGroups: ReadonlyArray<DiagnosticsTargetGroup>;
  readonly activeTarget: DiagnosticsTargetOption | null;
  readonly onSelectTarget: (option: DiagnosticsTargetOption) => void;
  readonly eventsActive: boolean;
  readonly onShowRuntime: () => void;
  readonly onShowEvents: () => void;
}) {
  /**
   * The strip used to carry one tab per connected device plus Cloud, which
   * conflated two questions. It now answers only "which view", and the picker
   * beside Recheck answers "which machine".
   */
  const RuntimeIcon = activeTarget?.kind === "desktop" ? LaptopIcon : CloudIcon;
  const tabs = [
    {
      id: "runtime",
      label: "Environment",
      icon: RuntimeIcon,
      selected: !eventsActive,
      onSelect: onShowRuntime,
    },
    {
      id: "events",
      label: "Events",
      icon: ListIcon,
      selected: eventsActive,
      onSelect: onShowEvents,
    },
  ] as const;

  /**
   * The page's controls, sitting above the first section rather than in a
   * chrome bar of their own. The settings shell owns the page's width and
   * padding now, so a full-bleed bordered header here would be the only
   * element on any settings page that ignored both.
   */
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4">
      <div className="flex items-center gap-0.5 rounded-lg bg-muted/40 p-0.5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
                tab.selected
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={tab.onSelect}
            >
              <Icon className="size-3.5" aria-hidden="true" />
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <DiagnosticsTargetPicker
          groups={targetGroups}
          active={activeTarget}
          onSelect={onSelectTarget}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={refreshing || !canRefresh}
          onClick={onRefresh}
        >
          {refreshing ? (
            <LoaderIcon className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCwIcon className="size-4" aria-hidden="true" />
          )}
          {isPlatformAdmin ? "Probe" : "Refresh"}
        </Button>
      </div>
    </div>
  );
}
